'use client';

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Image from 'next/image';
import { useAuth } from '../../context/AuthContext';
import ProtectedRoute from '../../components/ProtectedRoute';
import styles from '../styles/Profile.module.css';
import { gsap } from 'gsap';
import SplitType from 'split-type';
import Navbar from '../components/Navbar';
import Menu from '../components/Menu';
import { ClientPurchaseModal } from '../components/ClientPurchaseModal';
import { ConnectButton } from '../components/ConnectButton';
import UserOrdersOverTimeChart from '../components/profile/UserOrdersOverTimeChart';
import UserReferralsOverTimeChart from '../components/profile/UserReferralsOverTimeChart';
import SignInModal from '../components/SignInModal';
import Link from 'next/link';
import ProfileTotals from '../components/profile/ProfileTotals';
import ReferAFriend from '../components/profile/ReferAFriend';
import MiniPresaleCTA from '../components/profile/MiniPresaleCTA';

// Generic Token Display Component (can be moved to a separate file later)
const GenericTokenDisplay = React.memo(({ title, tokens, loading, error, icon }) => {
  if (loading) {
    return (
      <div className={styles.loadingContainer} style={{ padding: '2rem 0' }}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading {title.toLowerCase()}...</p>
      </div>
    );
  }
  if (error) {
    return <div className={styles.error}><p>{error}</p></div>;
  }
  if (!tokens || tokens.length === 0) {
    return (
      <div className={`${styles.noDataMessage} ${styles.glassmorphism} ${styles.bentoItem}`} style={{padding: '2rem', textAlign: 'center'}}>
        <p>You have no {title.toLowerCase()} allocated.</p>
      </div>
    );
  }
  return (
    <div className={`${styles.tokenDisplaySection} ${styles.glassmorphism} ${styles.bentoItem}`}>
      <div className={styles.sectionHeader}>
        {icon && <div className={styles.sectionIcon}>{icon}</div>}
        <h2>{title}</h2>
      </div>
      {/* Desktop Table - Hidden on mobile (initially, can add mobile cards later if needed) */}
      <div className={`${styles.referralsTableContainer} ${styles.hideOnMobile}`}> {/* Reusing referrals table container style */}
        <table className={styles.referralsTable}> {/* Reusing referrals table style */}
          <thead>
            <tr>
              <th>Amount</th>
              <th>Date</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {tokens.map(token => (
              <tr key={token._id}>
                <td>{Number(token.tokenAmount).toLocaleString()}</td>
                <td>{new Date(token.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</td>
                <td>{token.reason || 'N/A'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card List - Hidden on desktop ( reusing referral card styling for consistency) */}
      <div className={`${styles.referralCardMobile} ${styles.showOnMobile}`}>
        {tokens.map(token => (
          <div key={`${token._id}-mobile`} className={styles.referralCardMobileItem}>
            <div className={styles.referralCardMobileRow}>
              <span className={styles.referralCardMobileLabel}>Amount:</span>
              <span className={styles.referralCardMobileValue}>{Number(token.tokenAmount).toLocaleString()}</span>
            </div>
            <div className={styles.referralCardMobileRow}>
              <span className={styles.referralCardMobileLabel}>Date:</span>
              <span className={styles.referralCardMobileValue}>{new Date(token.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            </div>
            {token.reason && (
                <div className={styles.referralCardMobileRow}>
                    <span className={styles.referralCardMobileLabel}>Reason:</span>
                    <span className={styles.referralCardMobileValue}>{token.reason}</span>
                </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
});
GenericTokenDisplay.displayName = 'GenericTokenDisplay';

// Helper function to format date to YYYY-MM-DD (moved from UserOrdersOverTimeChart)
const formatDateKey = (date) => {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    console.error("Invalid date passed to formatDateKey:", date);
    return 'invalid-date'; 
  }
  return date.toISOString().split("T")[0];
};

// --- Icon Components (adapted from EthGasFeeDisplay) ---
const EthereumIcon = () => (
  <svg viewBox="0 0 256 417" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid" className={styles.blockchainIcon}>
    <path fill="#343434" d="m127.961 0-2.795 9.5v275.668l2.795 2.79 127.962-75.638z"/>
    <path fill="#8C8C8C" d="M127.962 0L0 219.782l127.962 75.638V0z"/>
    <path fill="#3C3C3B" d="m127.961 303.485-2.795-2.79v113.094l2.795 3.075 127.962-188.988z"/>
    <path fill="#8C8C8C" d="m127.962 416.854V303.485L0 227.866z"/>
    <path fill="#141414" d="m127.961 287.958 127.962-75.638-127.962-58.162z"/>
    <path fill="#393939" d="m0 212.32l127.962 75.638v-133.8z"/>
  </svg>
);

const PolygonIcon = () => (
  <Image
    src="https://imagedelivery.net/LOBxX7gyPpyC22XVZKaoOw/46e533a5-19d7-41ee-279c-196a22adce00/public"
    alt="Polygon MATIC Logo"
    width={20}
    height={20}
    className={styles.blockchainIcon}
  />
);

const BaseIcon = () => (
  <Image
    src="/assets/Base_Symbol_Blue.svg"
    alt="Base Logo"
    width={20}
    height={20}
    className={styles.blockchainIcon}
  />
);

const SolanaIcon = () => (
  <Image
    src="https://cryptologos.cc/logos/solana-sol-logo.svg?v=032"
    alt="Solana SOL Logo"
    width={20}
    height={20}
    className={styles.blockchainIcon}
  />
);

// --- Helper to get appropriate icon ---
const getBlockchainIcon = (networkName, currencyName) => {
  const lowerNetwork = networkName?.toLowerCase();
  const lowerCurrency = currencyName?.toLowerCase();

  // Prioritize ETH_BASE for Base Icon
  if (lowerNetwork === 'base' || lowerCurrency === 'eth_base' || lowerNetwork === 'eth_base') return <BaseIcon />;
  // Then check for other specific networks
  if (lowerNetwork === 'polygon') return <PolygonIcon />;
  if (lowerNetwork === 'solana') return <SolanaIcon />;
  
  // Ethereum (ensure this doesn't wrongly catch eth_base if it wasn't caught above, though it should be)
  if (lowerNetwork === 'ethereum' || lowerCurrency === 'eth') return <EthereumIcon />;
  
  // Fallback for currencies if network is not specific enough
  if (lowerCurrency === 'matic') return <PolygonIcon />;
  if (lowerCurrency === 'sol') return <SolanaIcon />;

  return null; // No icon if no match
};
// --- End Helper ---

// Define ProfileContent as a standalone, memoized component
const ProfileContent = React.memo(({
  user,
  loading,
  error,
  orders,
  expandedOrders,
  toggleOrderExpansion,
  setShowPurchaseModal,
  showPurchaseModal,
  copySuccess,
  handleCopyReferral,
  userReferrals,
  referralsLoading,
  referralsError,
  pageRef,
  headerRef,
  profileInfoRef,
  orderHistoryRef,
  ordersContainerRef,
  referralCombinedSectionRef,
  chartContainerRef,
  referralsChartContainerRef,
  referralsReferItemRef,
  setShowSignInModal,
  totalTokensPurchased,
  totalReferralBonusTokens,
  grandTotalTokens,
  isTotalsLoading,
  ordersChartLabels,
  ordersChartDataPoints,
  ordersLoading,
  referralsChartLabels,
  referralsChartDataPoints,
  activeTab,
  setActiveTab,
  availableTabs,
  profileMiniCtaRef,
  teamTokens,
  teamTokensLoading,
  teamTokensError,
  advisorTokens,
  advisorTokensLoading,
  advisorTokensError,
  mooreTrainingTokens,
  mooreTrainingTokensLoading,
  mooreTrainingTokensError,
  advisorReferralBounties,
  advisorReferralBountiesLoading,
  advisorReferralBountiesError,
  bonusTokens,
  bonusTokensLoading,
  bonusTokensError,
  teamTokensRef,
  advisorTokensRef,
  mooreTrainingTokensRef,
  advisorReferralBountiesRef,
  bonusTokensRef,
}) => {
  // Loading state for main profile content
  if (loading && !user) { // Show main loading only if user data isn't available yet
    return (
      <div className={styles.container} ref={pageRef}>
        <h1 ref={headerRef} className={styles.profilePageHeader}>My Profile</h1>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>Loading your profile data...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null; // Or a minimal placeholder, ProfilePage will show the modal
  }
  
  // Memoize the orders chart display
  const ordersChartDisplay = useMemo(() => {
    return (
      <div 
        className={`${styles.referralsChartItem} ${styles.chartContainer} ${styles.glassmorphism} ${styles.bentoItem}`}
        style={{ marginBottom: '0' }}
        ref={chartContainerRef}
      > 
        <UserOrdersOverTimeChart 
          chartLabels={ordersChartLabels}
          chartDataPoints={ordersChartDataPoints}
          isLoading={ordersLoading}
        />
      </div>
    );
  }, [ordersChartLabels, ordersChartDataPoints, ordersLoading, chartContainerRef]);

  // Memoize the referrals chart display
  const referralsChartDisplay = useMemo(() => {
    return (
      <div
        className={`${styles.referralsChartItem} ${styles.chartContainer} ${styles.glassmorphism} ${styles.bentoItem}`}
        style={{ marginBottom: '0' }}
        ref={referralsChartContainerRef}
      >
        <UserReferralsOverTimeChart 
          chartLabels={referralsChartLabels}
          chartDataPoints={referralsChartDataPoints}
          isLoading={referralsLoading}
        />
      </div>
    );
  }, [referralsChartLabels, referralsChartDataPoints, referralsLoading, referralsChartContainerRef]);

  return (
    <div className={styles.container} ref={pageRef}>
      <h1 ref={headerRef} className={styles.profilePageHeader}>My Profile</h1>
      
      <ProfileTotals 
        totalTokensPurchased={totalTokensPurchased}
        totalReferralBonusTokens={totalReferralBonusTokens}
        grandTotalTokens={grandTotalTokens}
        isLoading={isTotalsLoading} 
      />

     

      {/* Account Info - Takes full width before tabs */}
      <div className={`${styles.profileInfo} ${styles.glassmorphism} ${styles.bentoItemStandalone}`} ref={profileInfoRef} >
        <div className={styles.sectionHeader}>
          <div className={styles.sectionIcon}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </div>
          <h2>Account Information</h2>
        </div>
        <div className={styles.userInfo}>
          <div className={styles.infoItemRow}>
            <div className={styles.infoItemHalf}>
              <span className={styles.infoItemLabelWithBar}>Email</span>
              <p>{user.email}</p>
            </div>
            <div className={styles.infoItemHalf}>
              <span className={styles.infoItemLabelWithBar}>Email Verification</span>
              <div className={`${styles.verificationStatus} ${user.verifiedEmail ? styles.verified : styles.notVerified}`}>
                {user.verifiedEmail ? 'Verified' : 'Not Verified'}
              </div>
            </div>
          </div>
          <div className={styles.infoItemRow}>
            <div className={styles.infoItemHalf}>
              <span className={styles.infoItemLabelWithBar}>Wallet Address</span>
              <div className={styles.addressContainer}>
                <p className={styles.address}>{user.walletAddress}</p>
                <ConnectButton userId={user?._id} dbUserWalletAddress={user?.walletAddress} />
              </div>
            </div>
            <div className={styles.infoItemHalf}>
              <span className={styles.infoItemLabelWithBar}>Your Referral Code</span>
              <div className={styles.referralCodeRow} style={{ marginTop: '0.5rem' }}>
                <span className={styles.referralCode}>{user?.referralCode || '—'}</span>
                {user?.referralCode && (
                  <button className={styles.copyButton} onClick={handleCopyReferral} type="button">
                    {copySuccess ? 'Copied!' : 'Copy'}
                  </button>
                )}
              </div>
              <p className={styles.referralHelp} style={{ marginTop: '0.5rem' }}>Share this code to earn rewards when friends buy GAM Tokens!</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className={styles.tabNavigation}>
        {availableTabs.map(tab => (
          <button
            key={tab.key}
            className={`${styles.tabButton} ${activeTab === tab.key ? styles.activeTab : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Conditionally rendered tab content grid */}
      <div className={styles.tabItemsGrid}>
        {activeTab === 'orders' && (
          <>
            {/* Top row for Orders: Chart and MiniPresaleCTA side by side */}
            <div className={styles.referralsTopRow}> {/* Reusing this class for 2-column layout */}
              {ordersChartDisplay} 
              <div 
                className={`${styles.referralsReferItem} ${styles.glassmorphism} ${styles.bentoItem}`} // Reusing classes for styling consistency
                ref={profileMiniCtaRef} // Assign new ref
              >
                <MiniPresaleCTA />
              </div>
            </div>

            {/* Order History table below the chart and CTA */}
            <div className={`${styles.orderHistory} ${styles.glassmorphism} ${styles.bentoItem}`} ref={orderHistoryRef}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionIcon}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                    <line x1="3" y1="6" x2="21" y2="6"></line>
                    <path d="M16 10a4 4 0 0 1-8 0"></path>
                  </svg>
                </div>
                <h2>Purchase History</h2>
              </div>
              
              {error && !loading && ( // Show order fetch error only if not in main loading and user exists
                <div className={styles.error}>
                   <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <p>{error}</p>
                </div>
              )}
              
              {loading && orders.length === 0 && ( // Show specific loading for orders if main page loaded but orders are fetching
                 <div className={styles.loadingContainer} style={{padding: '2rem 0'}}>
                    <div className={styles.loadingSpinner}></div>
                    <p>Loading your purchase history...</p>
                </div>
              )}

              {!loading && !error && orders.length === 0 && (
                 <div className={styles.noOrders}>
                    <p>You haven't made any purchases yet.</p>
                    <button onClick={() => setShowPurchaseModal(true)} className={styles.buyButtonPrompt}>
                    Buy GAM Tokens Now
                    </button>
                </div>
              )}

              {orders.length > 0 && (
                <div className={styles.ordersContainer} ref={ordersContainerRef}>
                  {orders.map(order => (
                    <div key={order.orderId || order._id} className={styles.orderCard}>
                      <div className={`${styles.orderHeader} ${!expandedOrders[order.orderId || order._id] ? styles.orderHeaderCollapsed : ''}`}>
                        <h3>Order ID: {order.orderId}</h3>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span className={`${styles.orderStatus} ${styles[order.status?.toLowerCase()]}`}>{order.status}</span>
                          <button 
                            onClick={() => toggleOrderExpansion(order.orderId || order._id)} 
                            className={styles.chevronButton}
                            aria-expanded={!!expandedOrders[order.orderId || order._id]}
                            aria-controls={`order-details-${order.orderId || order._id}`}
                          >
                            {expandedOrders[order.orderId || order._id] ? (
                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"></polyline></svg>
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                            )}
                          </button>
                        </div>
                      </div>
                      <div 
                        className={`${styles.orderDetails} ${expandedOrders[order.orderId || order._id] ? styles.orderDetailsExpanded : ''}`}
                        id={`order-details-${order.orderId || order._id}`}
                        aria-hidden={!expandedOrders[order.orderId || order._id]}
                      >
                        {expandedOrders[order.orderId || order._id] && (
                          <>
                            <p><strong>Purchase Date:</strong> {new Date(order.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                            <p><strong>GAM Tokens:</strong> {order.gamTokensToCredit ? Number(order.gamTokensToCredit).toLocaleString(undefined, {maximumFractionDigits: 2}) : 'N/A'}</p>
                            <p><strong>Amount (USD):</strong> ${order.amountUsdEquivalent ? Number(order.amountUsdEquivalent).toFixed(2) : 'N/A'}</p>
                            <p><strong>Source:</strong> {order.source === 'moonpay' ? 'Card (via MoonPay)' : order.source === 'direct_crypto' ? 'Direct Crypto' : order.source || 'N/A'}</p>
                            
                            {order.paymentType === 'fiat_card' && order.fiatCurrency && (
                                <p><strong>Payment:</strong> {order.fiatAmount?.toFixed(2)} {order.fiatCurrency}</p>
                            )}
                            
                            {order.paymentType === 'crypto' && order.cryptoPaymentCurrency && (
                              <p>
                                <strong>Payment:</strong>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                                  {getBlockchainIcon(order.network, order.cryptoPaymentCurrency)}
                                  <span>
                                    {order.cryptoAmountPaid?.toFixed(6)}{' '}
                                    {order.cryptoPaymentCurrency === 'ETH_BASE' ? 'ETH Base' : order.cryptoPaymentCurrency}
                                  </span>
                                </span>
                              </p>
                            )}

                            {order.onChainTransactionHash && (
                              <p>
                                <strong>Tx Hash:</strong> 
                                <a 
                                  href={
                                    (order.network?.toLowerCase() === 'base' || order.cryptoPaymentCurrency === 'ETH_BASE')
                                      ? `https://basescan.org/tx/${order.onChainTransactionHash}`
                                      : `https://etherscan.io/tx/${order.onChainTransactionHash}`
                                  }
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className={styles.transactionLink}
                                >
                                  {`${order.onChainTransactionHash.substring(0, 6)}...${order.onChainTransactionHash.substring(order.onChainTransactionHash.length - 4)}`}
                                </a>
                              </p>
                            )}
                            {order.moonpayTransactionId && !order.onChainTransactionHash && (
                                <p><strong>MoonPay ID:</strong> {order.moonpayTransactionId}</p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
        {activeTab === 'referrals' && (
          <>
            {/* Top row: Chart and ReferAFriend side by side on desktop, stacked on mobile */}
            <div className={styles.referralsTopRow}>
              <div className={`${styles.referralsChartItem} ${styles.chartContainer} ${styles.glassmorphism} ${styles.bentoItem}`} ref={referralsChartContainerRef}>
                <UserReferralsOverTimeChart 
                  chartLabels={referralsChartLabels}
                  chartDataPoints={referralsChartDataPoints}
                  isLoading={referralsLoading}
                />
              </div>
              <div className={styles.referralsReferItem} ref={referralsReferItemRef}>
                <ReferAFriend userReferralCode={user.referralCode} userEmail={user.email} />
              </div>
            </div>
            <div className={`${styles.referredUsersSection} ${styles.glassmorphism} ${styles.bentoItem}`} ref={referralCombinedSectionRef}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionIcon}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
                <h2>Users You've Referred</h2>
              </div>

              {referralsLoading && (
                <div className={styles.loadingContainer}>
                  <div className={styles.loadingSpinner}></div>
                  <p>Loading your referrals...</p>
                </div>
              )}
              {referralsError && (
                <div className={styles.error}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                <p>{referralsError}</p>
                </div>
              )}
              {!referralsLoading && !referralsError && (
                userReferrals.length === 0 ? (
                  <div className={styles.noReferrals}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '1rem', opacity: 0.5 }}>
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M19 8v6" />
                      <path d="M22 11h-6" />
                    </svg>
                    <p>No one has used your referral code yet.</p>
                    <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', opacity: 0.7 }}>Share your code to start earning rewards!</p>
                  </div>
                ) : (
                  <>
                    {/* Desktop Table - Hidden on mobile */}
                    <div className={`${styles.referralsTableContainer} ${styles.hideOnMobile}`}> {/* Added hideOnMobile */}
                      <table className={styles.referralsTable}>
                        <thead>
                          <tr>
                            <th>Referred User</th>
                            <th>Date</th>
                            <th>Order ID</th>
                            <th>Value (USD)</th>
                            <th>Tokens Bought</th>
                            <th>Bonus Earned</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userReferrals.map(referral => (
                            <tr key={referral._id}>
                              <td>{referral.referredUserIdentifier}</td>
                              <td>{new Date(referral.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                              <td>
                                <span title={referral.orderId}>
                                  {referral.orderId.substring(0,8)}...
                                </span>
                              </td>
                              <td>${Number(referral.purchaseAmountUsd).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                              <td>{Number(referral.purchaseTokenAmount).toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
                              <td>
                                {referral.referralBonusTokens !== undefined ? (
                                  <span style={{ color: '#36D300', fontWeight: '600' }}>
                                    {Number(referral.referralBonusTokens).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                  </span>
                                ) : 'N/A'}
                              </td>
                              <td className={`${styles.referralStatus} ${styles[referral.status?.toLowerCase().replace(/\s+/g, '_')]}`}>
                                {referral.status}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Card List - Hidden on desktop */}
                    <div className={`${styles.referralCardMobile} ${styles.showOnMobile}`}> {/* Added showOnMobile */}
                      {userReferrals.map(referral => (
                        <div key={`${referral._id}-mobile`} className={styles.referralCardMobileItem}>
                          <div className={styles.referralCardMobileRow}>
                            <span className={styles.referralCardMobileLabel}>Referred User</span>
                            <span className={styles.referralCardMobileValue}>{referral.referredUserIdentifier}</span>
                          </div>
                          <div className={styles.referralCardMobileRow}>
                            <span className={styles.referralCardMobileLabel}>Date</span>
                            <span className={styles.referralCardMobileValue}>{new Date(referral.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          </div>
                          <div className={styles.referralCardMobileRow}>
                            <span className={styles.referralCardMobileLabel}>Order ID</span>
                            <span className={styles.referralCardMobileValue} title={referral.orderId}>{referral.orderId.substring(0,8)}...</span>
                          </div>
                          <div className={styles.referralCardMobileRow}>
                            <span className={styles.referralCardMobileLabel}>Value (USD)</span>
                            <span className={styles.referralCardMobileValue}>${Number(referral.purchaseAmountUsd).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                          </div>
                          <div className={styles.referralCardMobileRow}>
                            <span className={styles.referralCardMobileLabel}>Tokens Bought</span>
                            <span className={styles.referralCardMobileValue}>{Number(referral.purchaseTokenAmount).toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                          </div>
                          <div className={styles.referralCardMobileRow}>
                            <span className={styles.referralCardMobileLabel}>Bonus Earned</span>
                            <span className={styles.referralCardMobileValue}>
                              {referral.referralBonusTokens !== undefined ? (
                                  <span style={{ color: '#36D300', fontWeight: '600' }}>
                                    {Number(referral.referralBonusTokens).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                  </span>
                                ) : 'N/A'}
                            </span>
                          </div>
                          <div className={styles.referralCardMobileRow}>
                            <span className={styles.referralCardMobileLabel}>Status</span>
                            <span className={`${styles.referralCardMobileValue} ${styles.referralStatus} ${styles[referral.status?.toLowerCase().replace(/\s+/g, '_')]}`}>
                              {referral.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )
              )}
            </div>
          </>
        )}
        {activeTab === 'teamTokens' && (
          <div ref={teamTokensRef}>
            <GenericTokenDisplay
              title="Team Tokens"
              tokens={teamTokens}
              loading={teamTokensLoading}
              error={teamTokensError}
              icon={<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path><path d="M12 11l0 6"></path><path d="M12 8H12.01"></path></svg>}
            />
          </div>
        )}
        {activeTab === 'advisorTokens' && (
          <div ref={advisorTokensRef}>
            <GenericTokenDisplay
              title="Advisor Tokens"
              tokens={advisorTokens}
              loading={advisorTokensLoading}
              error={advisorTokensError}
              icon={<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="8.5" cy="7" r="4"></circle><polyline points="17 11 19 13 23 9"></polyline></svg>}
            />
          </div>
        )}
        {activeTab === 'mooreTrainingTokens' && (
          <div ref={mooreTrainingTokensRef}>
            <GenericTokenDisplay
              title="Moore Training Tokens"
              tokens={mooreTrainingTokens}
              loading={mooreTrainingTokensLoading}
              error={mooreTrainingTokensError}
              icon={<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg>}
            />
          </div>
        )}
        {activeTab === 'advisorReferralBounties' && (
          <div ref={advisorReferralBountiesRef}>
            <GenericTokenDisplay
              title="Advisor Referral Bounties"
              tokens={advisorReferralBounties}
              loading={advisorReferralBountiesLoading}
              error={advisorReferralBountiesError}
              icon={<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line></svg>}
            />
          </div>
        )}
        {activeTab === 'bonusTokens' && (
          <div ref={bonusTokensRef}>
            <GenericTokenDisplay
              title="Bonus Tokens"
              tokens={bonusTokens}
              loading={bonusTokensLoading}
              error={bonusTokensError}
              icon={<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"></path></svg>}
            />
          </div>
        )}
      </div> {/* End of tabItemsGrid */}

      <ClientPurchaseModal 
        isOpen={showPurchaseModal} 
        onClose={() => setShowPurchaseModal(false)} 
      />
    </div>
  );
});
ProfileContent.displayName = 'ProfileContent'; // Good for debugging

export default function ProfilePage() {
  const { user, loading, error, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [orders, setOrders] = useState([]);
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);

  const [userReferrals, setUserReferrals] = useState([]);
  const [referralsLoading, setReferralsLoading] = useState(true);
  const [referralsError, setReferralsError] = useState(null);
  
  const [activeTab, setActiveTab] = useState('orders');

  // State for profile totals - calculated from orders and referrals
  const [totalTokensPurchased, setTotalTokensPurchased] = useState(0);
  const [totalReferralBonusTokens, setTotalReferralBonusTokens] = useState(0);
  const [grandTotalTokens, setGrandTotalTokens] = useState(0);
  
  // State for processed UserOrdersOverTimeChart data
  const [ordersChartLabels, setOrdersChartLabels] = useState([]);
  const [ordersChartDataPoints, setOrdersChartDataPoints] = useState([]);

  // State for processed UserReferralsOverTimeChart data
  const [referralsChartLabels, setReferralsChartLabels] = useState([]);
  const [referralsChartDataPoints, setReferralsChartDataPoints] = useState([]);

  // State for new token types
  const [teamTokens, setTeamTokens] = useState([]);
  const [teamTokensLoading, setTeamTokensLoading] = useState(true);
  const [teamTokensError, setTeamTokensError] = useState(null);

  const [advisorTokens, setAdvisorTokens] = useState([]);
  const [advisorTokensLoading, setAdvisorTokensLoading] = useState(true);
  const [advisorTokensError, setAdvisorTokensError] = useState(null);

  const [mooreTrainingTokens, setMooreTrainingTokens] = useState([]);
  const [mooreTrainingTokensLoading, setMooreTrainingTokensLoading] = useState(true);
  const [mooreTrainingTokensError, setMooreTrainingTokensError] = useState(null);

  const [advisorReferralBounties, setAdvisorReferralBounties] = useState([]);
  const [advisorReferralBountiesLoading, setAdvisorReferralBountiesLoading] = useState(true);
  const [advisorReferralBountiesError, setAdvisorReferralBountiesError] = useState(null);

  const [bonusTokens, setBonusTokens] = useState([]);
  const [bonusTokensLoading, setBonusTokensLoading] = useState(true);
  const [bonusTokensError, setBonusTokensError] = useState(null);

  const pageRef = useRef(null);
  const headerRef = useRef(null);
  const profileInfoRef = useRef(null);
  const orderHistoryRef = useRef(null);
  const ordersContainerRef = useRef(null);
  const referralCombinedSectionRef = useRef(null);
  const chartContainerRef = useRef(null);
  const referralsChartContainerRef = useRef(null);
  const referralsReferItemRef = useRef(null);
  const profileMiniCtaRef = useRef(null);
  // Refs for new sections
  const teamTokensRef = useRef(null);
  const advisorTokensRef = useRef(null);
  const mooreTrainingTokensRef = useRef(null);
  const advisorReferralBountiesRef = useRef(null);
  const bonusTokensRef = useRef(null);

  // Generic fetch function for token allocations
  const fetchUserAllocations = useCallback(async (apiType, setData, setLoading, setError, userId) => {
    if (!userId) {
      setData([]);
      setLoading(false);
      setError(null); // Clear error on logout/no user
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/user-allocations?type=${apiType}&userId=${userId}`);
      const result = await response.json();
      if (result.success && Array.isArray(result.data)) {
        setData(result.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      } else {
        setError(result.message || `Failed to fetch ${apiType.replace('-', ' ')}.`);
        setData([]);
      }
    } catch (err) {
      console.error(`Error fetching ${apiType}:`, err);
      setError(`An error occurred while fetching ${apiType.replace('-', ' ')}.`);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(prev => !prev);
  };

  const toggleOrderExpansion = useCallback((orderId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  }, []);

  const handleCopyReferral = useCallback(() => {
    if (user?.referralCode) {
      navigator.clipboard.writeText(user.referralCode);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 1200);
    }
  }, [user?.referralCode]);

  useEffect(() => {
    if (!loading && !user) {
      setShowSignInModal(true);
    }
    if (user) {
      setShowSignInModal(false);
    }
  }, [user, loading]);

  useEffect(() => {
    async function fetchOrders() {
      if (!user?._id) {
        setOrders([]);
        setOrdersLoading(false);
        return;
      }
      setOrdersLoading(true);
      setOrdersError(null);
      try {
        const response = await fetch(`/api/orders/user?userId=${user._id}`);
        const data = await response.json();

        if (data.success && Array.isArray(data.data.orders)) {
          const sortedOrders = data.data.orders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          setOrders(sortedOrders);
          if (sortedOrders.length > 0) {
            setExpandedOrders(prev => ({
              ...prev,
              [sortedOrders[0].orderId || sortedOrders[0]._id]: prev[sortedOrders[0].orderId || sortedOrders[0]._id] !== undefined ? prev[sortedOrders[0].orderId || sortedOrders[0]._id] : true
            }));
          }
        } else {
          setOrdersError(data.message || 'Failed to fetch your orders.');
          setOrders([]);
        }
      } catch (err) {
        setOrdersError('An error occurred while fetching your orders');
        setOrders([]);
      } finally {
        setOrdersLoading(false);
      }
    }

    if (user?._id) {
      fetchOrders();
    } else if (user === null) {
      setOrders([]);
      setOrdersError(null);
      setOrdersLoading(false);
    }
  }, [user?._id]);

  useEffect(() => {
    async function fetchUserReferrals() {
      if (!user?._id) {
        setUserReferrals([]);
        setReferralsLoading(false);
        return;
      }
      setReferralsLoading(true);
      setReferralsError(null);
      try {
        const response = await fetch(`/api/referrals/by-user?userId=${user._id}`);
        const data = await response.json();
        if (data.success && Array.isArray(data.data)) {
          setUserReferrals(data.data);
        } else {
          setReferralsError(data.message || 'Failed to fetch your referrals.');
          setUserReferrals([]);
        }
      } catch (err) {
        setReferralsError('An error occurred while fetching your referrals.');
        setUserReferrals([]);
      } finally {
        setReferralsLoading(false);
      }
    }

    if (user?._id) {
      fetchUserReferrals();
      // Fetch new token types
      fetchUserAllocations('team-tokens', setTeamTokens, setTeamTokensLoading, setTeamTokensError, user._id);
      fetchUserAllocations('advisor-tokens', setAdvisorTokens, setAdvisorTokensLoading, setAdvisorTokensError, user._id);
      fetchUserAllocations('moore-training-tokens', setMooreTrainingTokens, setMooreTrainingTokensLoading, setMooreTrainingTokensError, user._id);
      fetchUserAllocations('advisor-referral-bounties', setAdvisorReferralBounties, setAdvisorReferralBountiesLoading, setAdvisorReferralBountiesError, user._id);
      fetchUserAllocations('bonus-tokens', setBonusTokens, setBonusTokensLoading, setBonusTokensError, user._id);
    } else if (user === null) { // Clear data if user logs out or is not found
      setUserReferrals([]); setReferralsError(null); setReferralsLoading(false);
      setTeamTokens([]); setTeamTokensError(null); setTeamTokensLoading(false);
      setAdvisorTokens([]); setAdvisorTokensError(null); setAdvisorTokensLoading(false);
      setMooreTrainingTokens([]); setMooreTrainingTokensError(null); setMooreTrainingTokensLoading(false);
      setAdvisorReferralBounties([]); setAdvisorReferralBountiesError(null); setAdvisorReferralBountiesLoading(false);
      setBonusTokens([]); setBonusTokensError(null); setBonusTokensLoading(false);
    }
  }, [user?._id, fetchUserAllocations]); // Added fetchUserAllocations to dependency array

  // Calculate totals whenever orders or referrals data changes
  useEffect(() => {
    if (orders && orders.length > 0) {
      const purchased = orders.reduce((acc, order) => {
        // Assuming gamTokensToCredit is the field for tokens in an order
        // And status is 'paid' or 'completed'
        if ((order.status?.toLowerCase() === 'paid' || order.status?.toLowerCase() === 'completed') && order.gamTokensToCredit) {
          return acc + Number(order.gamTokensToCredit);
        }
        return acc;
      }, 0);
      setTotalTokensPurchased(purchased);
    } else {
      setTotalTokensPurchased(0);
    }

    if (userReferrals && userReferrals.length > 0) {
      const bonus = userReferrals.reduce((acc, referral) => {
        // Assuming referralBonusTokens is the field and status indicates it's a valid bonus
        if ((referral.status?.toLowerCase() === 'reward_processed' || referral.status?.toLowerCase() === 'pending_reward') && referral.referralBonusTokens) {
          return acc + Number(referral.referralBonusTokens);
        }
        return acc;
      }, 0);
      setTotalReferralBonusTokens(bonus);
    } else {
      setTotalReferralBonusTokens(0);
    }
  }, [orders, userReferrals]);

  useEffect(() => {
    const sumTokenArray = (tokensArray) => {
      if (tokensArray && tokensArray.length > 0) {
        return tokensArray.reduce((sum, token) => {
          // Ensure tokenAmount is a number, default to 0 if not or undefined
          const amount = Number(token.tokenAmount);
          return sum + (isNaN(amount) ? 0 : amount);
        }, 0);
      }
      return 0;
    };

    const sumTeam = sumTokenArray(teamTokens);
    const sumAdvisor = sumTokenArray(advisorTokens);
    const sumMooreTraining = sumTokenArray(mooreTrainingTokens);
    const sumAdvisorReferral = sumTokenArray(advisorReferralBounties);
    const sumBonus = sumTokenArray(bonusTokens);

    setGrandTotalTokens(
      totalTokensPurchased +
      totalReferralBonusTokens +
      sumTeam +
      sumAdvisor +
      sumMooreTraining +
      sumAdvisorReferral +
      sumBonus
    );
  }, [
    totalTokensPurchased,
    totalReferralBonusTokens,
    teamTokens,
    advisorTokens,
    mooreTrainingTokens,
    advisorReferralBounties,
    bonusTokens,
  ]);

  // Process orders for UserOrdersOverTimeChart
  useEffect(() => {
    if (!orders || orders.length === 0) {
      setOrdersChartLabels([]);
      setOrdersChartDataPoints([]);
      return;
    }

    const paidOrCompletedOrders = orders.filter(
      (order) =>
        order.status &&
        (order.status.toLowerCase() === "paid" || order.status.toLowerCase() === "completed") &&
        order.createdAt &&
        !isNaN(new Date(order.createdAt).getTime())
    );
    const sortedOrders = [...paidOrCompletedOrders].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    const orderCountsByDate = {};
    let cumulativeCount = 0;
    sortedOrders.forEach((order) => {
      try {
        const dateKey = formatDateKey(new Date(order.createdAt));
        if (dateKey !== 'invalid-date') {
            orderCountsByDate[dateKey] = (orderCountsByDate[dateKey] || 0) + 1;
        }
      } catch (e) {
        console.error("Error processing date for order:", order, e);
      }
    });
    const uniqueDates = Object.keys(orderCountsByDate).sort();
    const labels = [];
    const dataPoints = [];
    uniqueDates.forEach((dateKey) => {
      cumulativeCount += orderCountsByDate[dateKey];
      labels.push(dateKey);
      dataPoints.push(cumulativeCount);
    });
    
    setOrdersChartLabels(labels);
    setOrdersChartDataPoints(dataPoints);

  }, [orders]); // Depends on the raw orders data fetched by the page

  // Process referrals for UserReferralsOverTimeChart
  useEffect(() => {
    if (!userReferrals || userReferrals.length === 0) {
      setReferralsChartLabels([]);
      setReferralsChartDataPoints([]);
      return;
    }

    const validReferrals = userReferrals.filter(
      (ref) =>
        ref.status && 
        (ref.status.toLowerCase() === "pending_reward" || ref.status.toLowerCase() === "reward_processed") &&
        ref.createdAt &&
        !isNaN(new Date(ref.createdAt).getTime())
    );
    const sortedReferrals = [...validReferrals].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    const referralCountsByDate = {};
    let cumulativeCount = 0;
    sortedReferrals.forEach((ref) => {
      try {
        const dateKey = formatDateKey(new Date(ref.createdAt));
        if (dateKey !== 'invalid-date') {
          referralCountsByDate[dateKey] = (referralCountsByDate[dateKey] || 0) + 1;
        }
      } catch (e) {
        console.error("Error processing date for referral:", ref, e);
      }
    });
    const uniqueDates = Object.keys(referralCountsByDate).sort();
    const labels = [];
    const dataPoints = [];
    uniqueDates.forEach((dateKey) => {
      cumulativeCount += referralCountsByDate[dateKey];
      labels.push(dateKey);
      dataPoints.push(cumulativeCount);
    });
    
    setReferralsChartLabels(labels);
    setReferralsChartDataPoints(dataPoints);

  }, [userReferrals]); // Depends on the raw userReferrals data fetched by the page

  useEffect(() => {
    if (loading || !pageRef.current) return;

    // Common elements to animate
    const commonElements = [headerRef.current].filter(Boolean);

    // Elements specific to the active tab
    let activeTabElements = [];
    let activeTabOrderCards = [];

    if (activeTab === 'orders') {
      activeTabElements = [
        profileInfoRef.current, 
        chartContainerRef.current, 
        profileMiniCtaRef.current,
        orderHistoryRef.current    
      ].filter(Boolean);
      if (ordersContainerRef.current && orders.length > 0) {
        activeTabOrderCards = Array.from(ordersContainerRef.current.querySelectorAll(`.${styles.orderCard}`));
      }
    } else if (activeTab === 'referrals') {
      activeTabElements = [
        profileInfoRef.current, 
        referralsChartContainerRef.current, 
        referralsReferItemRef.current,
        referralCombinedSectionRef.current  
      ].filter(Boolean);
    } else if (activeTab === 'teamTokens') {
        activeTabElements = [profileInfoRef.current, teamTokensRef.current].filter(Boolean);
    } else if (activeTab === 'advisorTokens') {
        activeTabElements = [profileInfoRef.current, advisorTokensRef.current].filter(Boolean);
    } else if (activeTab === 'mooreTrainingTokens') {
        activeTabElements = [profileInfoRef.current, mooreTrainingTokensRef.current].filter(Boolean);
    } else if (activeTab === 'advisorReferralBounties') {
        activeTabElements = [profileInfoRef.current, advisorReferralBountiesRef.current].filter(Boolean);
    } else if (activeTab === 'bonusTokens') {
        activeTabElements = [profileInfoRef.current, bonusTokensRef.current].filter(Boolean);
    }

    const pageElements = [...commonElements, ...activeTabElements];

    // Ensure elements are initially positioned for animation but remain hidden by CSS opacity
    if (pageElements.length > 0) {
      gsap.set(pageElements, { y: 20 }); // Set initial y transform only
      gsap.to(pageElements, {
        autoAlpha: 1, // Fades in and sets visibility to visible
        y: 0,
        duration: 0.8,
        ease: 'power3.out',
        stagger: 0.15
      });
    }
    
    if (activeTabOrderCards.length > 0) {
      gsap.set(activeTabOrderCards, { y: 15 }); // Set initial y transform only
      gsap.to(activeTabOrderCards, {
        autoAlpha: 1, // Fades in and sets visibility to visible
        y: 0,
        stagger: 0.08,
        duration: 0.5,
        ease: 'power2.out',
        delay: 0.5 
      });
    }
    
    return () => {
      const elementsToKill = [...pageElements, ...activeTabOrderCards];
      if (elementsToKill.length > 0) {
        gsap.killTweensOf(elementsToKill);
        // It's often better to let GSAP handle clearing props by new tweens rather than manually clearing all.
        // If flickering or state issues persist, explicit clearProps can be tried:
        // gsap.set(elementsToKill, { clearProps: "all" }); 
      }
    };
  }, [user, loading, orders.length, activeTab, styles.orderCard]); // Added activeTab and styles.orderCard

  const availableTabs = useMemo(() => {
    const tabs = [
      { 
        key: 'orders', 
        label: 'Purchases', 
        icon: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path><line x1="3" y1="6" x2="21" y2="6"></line><path d="M16 10a4 4 0 0 1-8 0"></path></svg> 
      },
      { 
        key: 'referrals', 
        label: 'Referrals', 
        icon: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
      }
    ];
    // Conditionally add other tabs
    // Show tab if data exists OR if it's currently loading (to avoid flicker)
    if (!teamTokensLoading && teamTokens.length > 0) tabs.push({ key: 'teamTokens', label: 'Team', icon: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path><path d="M12 11l0 6"></path><path d="M12 8H12.01"></path></svg>});
    if (!advisorTokensLoading && advisorTokens.length > 0) tabs.push({ key: 'advisorTokens', label: 'Advisor', icon: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="8.5" cy="7" r="4"></circle><polyline points="17 11 19 13 23 9"></polyline></svg> });
    if (!mooreTrainingTokensLoading && mooreTrainingTokens.length > 0) tabs.push({ key: 'mooreTrainingTokens', label: 'Training', icon: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg> });
    if (!advisorReferralBountiesLoading && advisorReferralBounties.length > 0) tabs.push({ key: 'advisorReferralBounties', label: 'Bounties', icon: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line></svg> });
    if (!bonusTokensLoading && bonusTokens.length > 0) tabs.push({ key: 'bonusTokens', label: 'Bonus', icon: <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"></path></svg> });
    return tabs;
  }, [
    userReferrals.length, referralsLoading, // Keep referrals tab logic simple for now or refine later
    teamTokens.length, teamTokensLoading,
    advisorTokens.length, advisorTokensLoading,
    mooreTrainingTokens.length, mooreTrainingTokensLoading,
    advisorReferralBounties.length, advisorReferralBountiesLoading,
    bonusTokens.length, bonusTokensLoading
  ]);

  // Ensure activeTab is valid if the available tabs change
  useEffect(() => {
    if (loading) return; // Don't change tabs while main user data is loading

    const currentTabIsValid = availableTabs.some(tab => tab.key === activeTab);
    if (!currentTabIsValid && availableTabs.length > 0) {
      setActiveTab(availableTabs[0].key);
    } else if (availableTabs.length === 1 && availableTabs[0].key === 'orders' && activeTab !== 'orders') { 
      // If only 'orders' tab is left (e.g. user has no referrals or other tokens)
      setActiveTab('orders');
    }
  }, [availableTabs, activeTab, loading]);

  const handleCloseModalAndRefresh = () => {
    setShowPurchaseModal(false);
    fetchOrders();
  };

  if (loading && !user) {
    return (
      <div className={styles.pageContainer}>
        <Navbar toggleMenu={toggleMenu} isMenuOpen={isMenuOpen} />
        <Menu isOpen={isMenuOpen} />
        <div className={styles.container} ref={pageRef}>
          <h1 ref={headerRef} className={styles.profilePageHeader}>My Profile</h1>
          <div className={styles.loadingContainer} style={{minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
            <div className={styles.loadingSpinner}></div>
            <p>Loading your profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (showSignInModal) {
    return (
      <div className={styles.pageContainer}> 
        <Navbar toggleMenu={toggleMenu} isMenuOpen={isMenuOpen} />
        <Menu isOpen={isMenuOpen} />
        <SignInModal 
          open={showSignInModal} 
          onClose={() => {
            setShowSignInModal(false);
          }} 
        />
        {!user && (
            <div className={styles.container} ref={pageRef} style={{textAlign: 'center', paddingTop: '5rem'}}>
                <h1 ref={headerRef} className={styles.profilePageHeader}>My Profile</h1>
                 <p>Please sign in to access your profile.</p>
            </div>
        )}
      </div>
    );
  }

  if (!user && !loading) {
    return (
        <div className={styles.pageContainer}>
            <Navbar toggleMenu={toggleMenu} isMenuOpen={isMenuOpen} />
            <Menu isOpen={isMenuOpen} />
            <div className={styles.container} ref={pageRef} style={{textAlign: 'center', paddingTop: '5rem'}}>
                <h1 ref={headerRef} className={styles.profilePageHeader}>My Profile</h1>
                <p>You need to be signed in to view this page.</p>
                 <Link href="/" className={styles.homeButtonLink}>Go to Homepage</Link>
            </div>
        </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <Navbar toggleMenu={toggleMenu} isMenuOpen={isMenuOpen} />
      <Menu isOpen={isMenuOpen} />
      <ProfileContent
        user={user}
        loading={loading}
        error={error}
        orders={orders}
        expandedOrders={expandedOrders}
        toggleOrderExpansion={toggleOrderExpansion}
        setShowPurchaseModal={setShowPurchaseModal}
        showPurchaseModal={showPurchaseModal}
        copySuccess={copySuccess}
        handleCopyReferral={handleCopyReferral}
        userReferrals={userReferrals}
        referralsLoading={referralsLoading}
        referralsError={referralsError}
        pageRef={pageRef}
        headerRef={headerRef}
        profileInfoRef={profileInfoRef}
        orderHistoryRef={orderHistoryRef}
        ordersContainerRef={ordersContainerRef}
        referralCombinedSectionRef={referralCombinedSectionRef}
        chartContainerRef={chartContainerRef}
        referralsChartContainerRef={referralsChartContainerRef}
        referralsReferItemRef={referralsReferItemRef}
        setShowSignInModal={setShowSignInModal}
        totalTokensPurchased={totalTokensPurchased}
        totalReferralBonusTokens={totalReferralBonusTokens}
        grandTotalTokens={grandTotalTokens}
        isTotalsLoading={loading || ordersLoading || referralsLoading || teamTokensLoading || advisorTokensLoading || mooreTrainingTokensLoading || advisorReferralBountiesLoading || bonusTokensLoading}
        ordersChartLabels={ordersChartLabels}
        ordersChartDataPoints={ordersChartDataPoints}
        ordersLoading={ordersLoading}
        referralsChartLabels={referralsChartLabels}
        referralsChartDataPoints={referralsChartDataPoints}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        availableTabs={availableTabs}
        profileMiniCtaRef={profileMiniCtaRef}
        teamTokens={teamTokens}
        teamTokensLoading={teamTokensLoading}
        teamTokensError={teamTokensError}
        advisorTokens={advisorTokens}
        advisorTokensLoading={advisorTokensLoading}
        advisorTokensError={advisorTokensError}
        mooreTrainingTokens={mooreTrainingTokens}
        mooreTrainingTokensLoading={mooreTrainingTokensLoading}
        mooreTrainingTokensError={mooreTrainingTokensError}
        advisorReferralBounties={advisorReferralBounties}
        advisorReferralBountiesLoading={advisorReferralBountiesLoading}
        advisorReferralBountiesError={advisorReferralBountiesError}
        bonusTokens={bonusTokens}
        bonusTokensLoading={bonusTokensLoading}
        bonusTokensError={bonusTokensError}
        teamTokensRef={teamTokensRef}
        advisorTokensRef={advisorTokensRef}
        mooreTrainingTokensRef={mooreTrainingTokensRef}
        advisorReferralBountiesRef={advisorReferralBountiesRef}
        bonusTokensRef={bonusTokensRef}
      />
    </div>
  );
} 